/* eslint-disable jsx-a11y/anchor-has-content */
import { countryHiringGuides } from "~/app/data/countryHiringGuides"
import type { TFunction } from "react-i18next"
import { Trans, useTranslation } from "react-i18next"
import htmlParser from "html-react-parser"
import type { BenefitsTier } from "@oysterhr/schema"
import { countryCodeToName } from "@oysterhr/common"
import { BenefitsHiringAdvisor } from "@oysterhr/benefits"
import { HiringTemplatesNote } from "../pages/hiringTemplates/_shared"
import { usePearlContext } from "@oysterhr/pearl"
import { SalaryInsight } from "../components/SalaryInsight"
import { EquityHiringAdvisor } from "@oysterhr/equity"
import type { HiringFTEPearlContext } from "~/shared/components/PearlTooltip/types"
import type { AdvisorSharedContextFragment } from "../contexts/AdvisorSharedContext"
import { useVatPearlText } from "~/shared/components/FormSections/VATManagementTeamMemberSection/helpers"
import type { VatManagementPearlFields } from "~/shared/components/FormSections/VATManagementTeamMemberSection/types"
import {
  JOB_DESCRIPTION_SUBSECTIONS_FIELDS,
  JobDescriptionSubsectionsAdvisor,
} from "@oysterhr/hiring/full-time-employee"

const options = { ns: "knowledge_base/Default" }

export const getCountrySpecificTranslation = (
  t: TFunction,
  countryCode?: string,
  showDefaultTextWithSpecific = true,
) => {
  // If there isnt' a selected country, we show the default message
  if (!countryCode) {
    return (
      <span className="text-base leading-normal text-black">
        {t("countryCode.default", options)}
      </span>
    )
  } else {
    // If the selected country isn't supported, we show the default message
    const countryHiringGuide = countryHiringGuides?.[countryCode]
    if (!countryHiringGuide) {
      if (!showDefaultTextWithSpecific) return ""
      return (
        <span className="text-base leading-normal text-black">
          {t("countryCode.default", options)}
        </span>
      )
    }
    // In Object.entries, the [0] position equals to the key, and the [1] position equals to the value
    const countryName = countryHiringGuide.name
    const hiringGuideUrl = countryHiringGuide.hiringGuideUrl

    return (
      <span className="text-base leading-normal text-black">
        {showDefaultTextWithSpecific && <p className="mb-4">{t("countryCode.default", options)}</p>}
        <Trans
          i18nKey="countryCode.countrySpecific"
          ns="knowledge_base/Default"
          values={{ countryName }}
          components={{
            link: (
              <a className="text-primary-base underline" href={hiringGuideUrl} target="_blank" />
            ),
          }}
        />
      </span>
    )
  }
}

export const getGenericMessage = (countryCode?: string) => {
  const countryName = countryCode ? countryHiringGuides?.[countryCode]?.name : ""
  const hiringGuideUrl = countryCode
    ? countryHiringGuides?.[countryCode]?.hiringGuideUrl
    : "https://www.oysterhr.com/global-hiring-guides"

  const quoteUrl = countryCode
    ? `https://app.oysterhr.com/quote?countryCode=${countryCode}`
    : "https://app.oysterhr.com/quote"

  return (
    <Trans i18nKey="generic" ns="knowledge_base/Default" values={{ countryName }}>
      <p className="mb-2 font-medium"></p>
      <a className="text-primary-base underline" href={hiringGuideUrl} target="_blank"></a>
      <a className="text-primary-base underline" href={quoteUrl} target="_blank"></a>
      <a
        className="text-primary-base underline"
        href="https://app.oysterhr.com/employer-cost-index"
        target="_blank"
      ></a>
    </Trans>
  )
}
type DefaultAdvisorFallbackProps = {
  employment: AdvisorSharedContextFragment | undefined
  countryCode: string | undefined
}
export const DefaultAdvisorFallback = ({
  employment,
  countryCode,
}: DefaultAdvisorFallbackProps): JSX.Element => {
  const { t } = useTranslation("knowledge_base/Default")

  const { focusedField, pearlExtraInputs: extraInputs } = usePearlContext<HiringFTEPearlContext>()

  const vatManagementPearlText = useVatPearlText(
    focusedField as VatManagementPearlFields,
    countryCode,
  )

  // Generic message
  if (!focusedField) {
    return <>{getGenericMessage(countryCode)}</>
  } else if (focusedField === "countryHighlights") {
    return <>{getCountrySpecificTranslation(t, countryCode, true)}</>
  } else if (focusedField === "costOfEmployment") {
    // Check if there are automated agreements
    const agreements = employment?.hiring?.agreements ?? []
    const hasAutomatedAgreements = agreements.some(a => a.isAutomated)
    const costofEmploymentKey = hasAutomatedAgreements
      ? "costOfEmploymentWithAgreements"
      : "costOfEmployment"
    return <Trans i18nKey={costofEmploymentKey} ns="knowledge_base/Default" />
  } else if (focusedField === "benefits.tier") {
    if (countryCode && extraInputs && extraInputs.tier) {
      return (
        <BenefitsHiringAdvisor
          countryCode={countryCode}
          tier={extraInputs.tier as BenefitsTier}
          salaryCurrencyCode={extraInputs.salaryCurrencyCode as string}
          countryBenefits={[]}
        />
      )
    } else {
      return getGenericMessage(countryCode)
    }
  } else if (focusedField === "variableCompensation.salaryAmount") {
    return <SalaryInsight />
  } else if (focusedField === "equity") {
    return <EquityHiringAdvisor countryCode={countryCode || ""} />
  } else if (focusedField?.startsWith("vatManagement")) {
    return <>{htmlParser(vatManagementPearlText || "")}</>
  } else if (focusedField?.startsWith("hiringTemplates")) {
    return (
      <div>
        <div>{htmlParser(t(`${focusedField}`))}</div>
        <HiringTemplatesNote />
      </div>
    )
  } else if (focusedField?.startsWith("hiringContractors")) {
    const countryName = countryCodeToName(countryCode)
    return <>{htmlParser(t(`${focusedField}`, { countryName }))}</>
  } else if (focusedField && JOB_DESCRIPTION_SUBSECTIONS_FIELDS.includes(focusedField)) {
    return <JobDescriptionSubsectionsAdvisor focusedField={focusedField} />
  } else {
    return <>{htmlParser(t(`${focusedField}`))}</>
  }
}
